/**
 * URL sanitising and traffic categorisation for the development-only network
 * meter (see ./meter.js). Pure functions, no browser state — unit-tested.
 *
 * Sanitising drops the query string and fragment outright (session tokens,
 * presigned-URL signatures and call ids all ride there) and replaces any path
 * segment that looks like an identifier with `:id`, so an exported report can be
 * shared without carrying a token or a patient/session id.
 */

// Numeric ids, UUIDs, long hex digests and long opaque base64url-ish slugs.
const ID_SEGMENT =
  /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,}|[A-Za-z0-9_-]{24,})$/i;

// A filename whose stem is an id keeps its extension, so `…/3f9a…c1.mp4` still
// reads as a video once sanitised.
const ID_FILENAME = /^(.+?)(\.[a-z0-9]{1,5})$/i;

const sanitizeSegment = segment => {
  if (!segment) {
    return segment;
  }
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch (_error) {
    // Keep the raw segment.
  }
  if (ID_SEGMENT.test(decoded)) {
    return ':id';
  }
  const fileMatch = decoded.match(ID_FILENAME);
  if (fileMatch && ID_SEGMENT.test(fileMatch[1])) {
    return `:id${fileMatch[2]}`;
  }
  return segment;
};

export function sanitizeUrl(rawUrl, base) {
  if (typeof rawUrl !== 'string' || rawUrl === '') {
    return 'unknown-url';
  }
  if (rawUrl.startsWith('data:')) {
    return 'data:';
  }
  if (rawUrl.startsWith('blob:')) {
    return 'blob:';
  }
  try {
    const url = new URL(rawUrl, base);
    const path = url.pathname.split('/').map(sanitizeSegment).join('/');
    return `${url.protocol}//${url.host}${path}`;
  } catch (_error) {
    return 'unparseable-url';
  }
}

const extensionOf = pathname => {
  const match = pathname.toLowerCase().match(/\.([a-z0-9]{1,5})$/);
  return match ? match[1] : '';
};

const VIDEO_EXT = new Set(['mp4', 'webm', 'mov', 'm4v', 'm3u8', 'ts', 'm4s', 'mpd']);
const AUDIO_EXT = new Set(['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'opus', 'flac']);
const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'ico', 'bmp']);
const FONT_EXT = new Set(['woff', 'woff2', 'ttf', 'otf', 'eot']);
const SCRIPT_EXT = new Set(['js', 'mjs', 'cjs', 'jsx', 'ts', 'tsx']);

/**
 * Category names used in reports. Kept as plain strings so an exported JSON is
 * readable without this file.
 */
const HTTP_CATEGORY = Object.freeze({
  document: 'document',
  appShell: 'app-shell',
  script: 'script',
  stylesheet: 'stylesheet',
  font: 'font',
  image: 'image',
  dataJson: 'data-json',
  poseModel: 'pose-model',
  poseWasm: 'pose-wasm',
  networkProbe: 'network-quality-probe',
  voiceSignaling: 'voice-signaling-http',
  backendApi: 'backend-api',
  mediaVideo: 'media-video',
  mediaAudio: 'media-audio',
  analytics: 'analytics-mixpanel',
  telemetry: 'telemetry-sentry',
  recaptcha: 'third-party-recaptcha',
  devServer: 'dev-server-modules',
  local: 'local-blob-or-data',
  other: 'other-http',
});

const WS_CATEGORY = Object.freeze({
  pose: 'pose-evaluation-ws',
  devHmr: 'dev-server-hmr-ws',
  other: 'other-ws',
});

// Traffic that only exists under `npm run dev`; reported, but kept out of the
// application totals because a built bundle never produces it.
export const DEV_ONLY_CATEGORIES = new Set([HTTP_CATEGORY.devServer, WS_CATEGORY.devHmr]);

// Not network traffic at all (blob:/data: reads).
export const NON_NETWORK_CATEGORIES = new Set([HTTP_CATEGORY.local]);

const hostMatches = (host, fragments) => fragments.some(fragment => host.includes(fragment));

/**
 * `context`: { pageOrigin, apiBaseUrl, isLocalDevServer }.
 * `initiatorType` is the PerformanceResourceTiming value when there is one
 * ('navigation' for the document itself), or null for a wrapped fetch/XHR.
 */
export function categorizeHttp(rawUrl, initiatorType, context = {}) {
  if (typeof rawUrl !== 'string') {
    return HTTP_CATEGORY.other;
  }
  if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) {
    return HTTP_CATEGORY.local;
  }
  if (initiatorType === 'navigation') {
    return HTTP_CATEGORY.document;
  }

  let url;
  try {
    url = new URL(rawUrl, context.pageOrigin);
  } catch (_error) {
    return HTTP_CATEGORY.other;
  }

  const host = url.host.toLowerCase();
  const path = url.pathname;
  const sameOrigin = context.pageOrigin ? url.origin === new URL(context.pageOrigin).origin : false;

  if (hostMatches(host, ['mixpanel.com', 'mxpnl.com'])) {
    return HTTP_CATEGORY.analytics;
  }
  if (hostMatches(host, ['sentry.io', 'sentry-cdn.com'])) {
    return HTTP_CATEGORY.telemetry;
  }
  if (
    hostMatches(host, ['recaptcha.net', 'gstatic.com', 'google.com']) &&
    !host.startsWith('fonts.')
  ) {
    return HTTP_CATEGORY.recaptcha;
  }

  if (sameOrigin) {
    if (path.startsWith('/models/')) {
      return HTTP_CATEGORY.poseModel;
    }
    if (path.startsWith('/wasm/')) {
      return HTTP_CATEGORY.poseWasm;
    }
    if (path.startsWith('/network-probe/')) {
      return HTTP_CATEGORY.networkProbe;
    }
    if (path === '/runtime-config.js' || path.endsWith('-sw.js') || path === '/worker.js') {
      return HTTP_CATEGORY.appShell;
    }
    if (
      context.isLocalDevServer &&
      (path.startsWith('/@') ||
        path.startsWith('/node_modules/') ||
        url.searchParams.has('import') ||
        url.searchParams.has('t') ||
        ['jsx', 'tsx', 'ts', 'scss', 'css', 'js', 'mjs'].includes(extensionOf(path)))
    ) {
      return HTTP_CATEGORY.devServer;
    }
  }

  if (context.apiBaseUrl) {
    try {
      const api = new URL(context.apiBaseUrl, context.pageOrigin);
      if (url.origin === api.origin && path.startsWith(api.pathname)) {
        return path.includes('/voice-companion/')
          ? HTTP_CATEGORY.voiceSignaling
          : HTTP_CATEGORY.backendApi;
      }
    } catch (_error) {
      // Unparseable API base: fall through to the extension rules.
    }
  }

  const ext = extensionOf(path);
  if (initiatorType === 'video' || VIDEO_EXT.has(ext)) {
    return HTTP_CATEGORY.mediaVideo;
  }
  if (initiatorType === 'audio' || AUDIO_EXT.has(ext)) {
    return HTTP_CATEGORY.mediaAudio;
  }
  if (initiatorType === 'img' || initiatorType === 'image' || IMAGE_EXT.has(ext)) {
    return HTTP_CATEGORY.image;
  }
  if (FONT_EXT.has(ext) || hostMatches(host, ['fonts.gstatic.com', 'fonts.googleapis.com'])) {
    return HTTP_CATEGORY.font;
  }
  if (ext === 'css' || (initiatorType === 'link' && ext === '')) {
    return HTTP_CATEGORY.stylesheet;
  }
  if (initiatorType === 'script' || SCRIPT_EXT.has(ext)) {
    return HTTP_CATEGORY.script;
  }
  if (ext === 'json') {
    return HTTP_CATEGORY.dataJson;
  }
  return HTTP_CATEGORY.other;
}

/**
 * `context`: { poseSocketUrl, pageOrigin }. `protocols` is what the page passed
 * to the WebSocket constructor (Vite's HMR client passes 'vite-hmr').
 */
export function categorizeWebSocket(rawUrl, protocols, context = {}) {
  const protocolList = Array.isArray(protocols) ? protocols : protocols ? [protocols] : [];
  if (protocolList.some(protocol => String(protocol).startsWith('vite'))) {
    return WS_CATEGORY.devHmr;
  }
  try {
    const url = new URL(rawUrl, context.pageOrigin);
    if (context.poseSocketUrl) {
      const pose = new URL(context.poseSocketUrl, context.pageOrigin);
      if (url.host === pose.host && url.pathname.startsWith(pose.pathname)) {
        return WS_CATEGORY.pose;
      }
    }
  } catch (_error) {
    // Fall through.
  }
  return WS_CATEGORY.other;
}
